import { useTranslation } from 'react-i18next';
import {
    useTheme,
    Box,
    Typography,
    IconButton,
    Icon,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    ChartTooltip,
} from '../../../../base';
import {
    IJobMetricChartProps,
    getJobMetricChartData,
    formatDurationTick,
    exportJobMetricsToExcel,
} from '../../../../common';
import { Directions } from '../../../../common/consts/system.consts';
import {
    StyledChartCard,
    StyledChartHeader,
    StyledLegendItem,
    StyledColorDot,
} from './JobSpecificView.styles';
import JobMetricChartTooltip from './JobMetricChartTooltip';

const JobLineChart = ({ data }: IJobMetricChartProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isRtl = theme.direction === Directions.rtl;

    const chartData = getJobMetricChartData(data, t);
    const totalDays = chartData.reduce((sum, datum) => sum + datum.days, 0);

    return (
        <StyledChartCard container direction="column">
            <StyledChartHeader>
                <Typography variant="body2" color={theme.palette.grey[600]}>
                    {`${t('reportsPage.timeToHireReport.jobSpecific.pieChartTitle')} - ${t('reportsPage.timeToHireReport.jobSpecific.totalHiringTime')} - ${totalDays} ${t('reportsPage.timeToHireReport.days')}`}
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(1),
                    }}
                >
                    <StyledLegendItem>
                        <StyledColorDot dotColor={theme.palette.primary.main} />
                        <Typography variant="body2">{data.jobTitle}</Typography>
                    </StyledLegendItem>
                    <IconButton
                        size="small"
                        onClick={() => exportJobMetricsToExcel(data, t)}
                    >
                        <Icon name="FileDownloadOutlinedIcon" size={16} />
                    </IconButton>
                </Box>
            </StyledChartHeader>
            <Box p={2}>
                <ResponsiveContainer width="100%" height={400}>
                    <LineChart
                        data={chartData}
                        margin={{
                            top: 40,
                            right: isRtl ? 5 : 40,
                            left: isRtl ? 40 : 5,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                            dataKey="name"
                            reversed={isRtl}
                            tick={{
                                fill: theme.palette.text.secondary,
                                fontSize: 12,
                            }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            domain={[0, 'auto']}
                            orientation={isRtl ? 'right' : 'left'}
                            tickFormatter={formatDurationTick}
                            tick={{
                                fill: theme.palette.text.secondary,
                                fontSize: 12,
                                textAnchor:'end',
                            }}
                            axisLine={false}
                            tickLine={false}
                            width={80}
                        />
                        <ChartTooltip
                            content={JobMetricChartTooltip as never}
                            cursor={{
                                stroke: theme.palette.grey[400],
                                strokeDasharray: '4 4',
                                strokeWidth: 1,
                            }}
                        />
                        <Line
                            type="monotone"
                            dataKey="days"
                            stroke={theme.palette.primary.main}
                            strokeWidth={2}
                            dot={{
                                r: 5,
                                fill: theme.palette.primary.main,
                                stroke: theme.palette.primary.main,
                            }}
                            activeDot={{ r: 7 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </Box>
        </StyledChartCard>
    );
};

export default JobLineChart;
